import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Modal,
  message,
  Tag,
  Space,
  Descriptions,
  Image,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  QRCode as AntQRCode,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  CopyOutlined,
  QrcodeOutlined,
  BankOutlined,
  WalletOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const CashIn = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [filters, setFilters] = useState({
    status: '',
    payment_method_type: '',
    date_from: null,
    date_to: null
  });

  // Modals
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Forms
  const [createForm] = Form.useForm();

  // Load data on component mount
  useEffect(() => {
    fetchTransactions();
    fetchPaymentMethods();
  }, []);

  // Fetch transactions
  const fetchTransactions = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        ...currentFilters,
        date_from: currentFilters.date_from ? dayjs(currentFilters.date_from).format('YYYY-MM-DD') : '',
        date_to: currentFilters.date_to ? dayjs(currentFilters.date_to).format('YYYY-MM-DD') : ''
      };

      const response = await axios.get('/api/cash-in/transactions', { params });

      if (response.data) {
        setTransactions(response.data.transactions || []);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      message.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available payment methods
  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get('/api/cash-in/payment-methods');
      if (response.data) {
        setPaymentMethods(response.data.payment_methods || {});
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  // Create new cash in transaction
  const handleCreateTransaction = async (values) => {
    try {
      setLoading(true);

      // Find selected payment method details
      const selectedMethod = Object.values(paymentMethods)
        .flat()
        .find(method => method.id === values.payment_method_id);

      const payload = {
        external_id: values.external_id,
        payment_method_type: selectedMethod?.type === 'virtual_account' && selectedMethod?.provider === 'QRIS_NATIONAL' ? 'qris' : selectedMethod?.type,
        payment_method_provider: selectedMethod?.provider,
        amount: values.amount,
        customer_name: values.customer_name,
        customer_email: values.customer_email,
        customer_phone: values.customer_phone,
        description: values.description,
        callback_url: values.callback_url,
        redirect_url: values.redirect_url,
        expires_in_minutes: values.expires_in_minutes || 60
      };

      const response = await axios.post('/api/cash-in/create', payload);

      if (response.data.success) {
        message.success('Cash in transaction created successfully');
        setCreateModalVisible(false);
        createForm.resetFields();
        fetchTransactions();

        // Show transaction details
        const newTxId = response.data.data.transaction_id;
        setTimeout(() => {
          viewTransactionDetails(newTxId);
        }, 1000);
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      message.error(error.response?.data?.error || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  // View transaction details
  const viewTransactionDetails = async (transactionId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/cash-in/transactions/${transactionId}`);

      if (response.data) {
        setSelectedTransaction(response.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      message.error('Failed to fetch transaction details');
    } finally {
      setLoading(false);
    }
  };

  // Simulate payment (for testing)
  const simulatePayment = async (transactionId) => {
    try {
      setLoading(true);
      const response = await axios.post(`/api/cash-in/transactions/${transactionId}/simulate-payment`);

      if (response.data.success) {
        message.success('Payment simulation completed');
        fetchTransactions();
        viewTransactionDetails(transactionId); // Refresh details
      }
    } catch (error) {
      console.error('Error simulating payment:', error);
      message.error(error.response?.data?.error || 'Failed to simulate payment');
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copied to clipboard`);
    });
  };

  // Status tag colors
  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'orange', icon: <ClockCircleOutlined /> },
      paid: { color: 'green', icon: <CheckCircleOutlined /> },
      expired: { color: 'red', icon: <ExclamationCircleOutlined /> },
      failed: { color: 'red', icon: <ExclamationCircleOutlined /> },
      cancelled: { color: 'default', icon: <ExclamationCircleOutlined /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {status.toUpperCase()}
      </Tag>
    );
  };

  // Payment method type icons
  const getPaymentMethodIcon = (type) => {
    const icons = {
      qris: <QrcodeOutlined style={{ color: '#1890ff' }} />,
      virtual_account: <BankOutlined style={{ color: '#52c41a' }} />,
      ewallet: <WalletOutlined style={{ color: '#722ed1' }} />
    };
    return icons[type] || <DollarOutlined />;
  };

  // Table columns
  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 150,
      render: (text) => (
        <Tooltip title="Click to copy">
          <Text
            copyable={{ text: text }}
            style={{ fontFamily: 'monospace', fontSize: '12px' }}
          >
            {text}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'External ID',
      dataIndex: 'external_id',
      key: 'external_id',
      width: 120
    },
    {
      title: 'Payment Method',
      key: 'payment_method',
      width: 180,
      render: (record) => (
        <Space>
          {getPaymentMethodIcon(record.payment_method_type)}
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
              {record.payment_method_name}
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.payment_method_type.replace('_', ' ').toUpperCase()}
            </div>
          </div>
        </Space>
      )
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            Rp {record.amount?.toLocaleString('id-ID')}
          </div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            Fee: Rp {record.fee?.toLocaleString('id-ID')}
          </div>
        </div>
      )
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 150,
      render: (text, record) => (
        <div>
          <div>{text || '-'}</div>
          {record.customer_email && (
            <div style={{ fontSize: '11px', color: '#666' }}>
              {record.customer_email}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (text) => (
        <div>
          <div>{dayjs(text).format('DD/MM/YYYY')}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>
            {dayjs(text).format('HH:mm:ss')}
          </div>
        </div>
      )
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            onClick={() => viewTransactionDetails(record.transaction_id)}
            size="small"
          >
            Detail
          </Button>
          {record.status === 'pending' && (
            <Button
              type="primary"
              size="small"
              onClick={() => simulatePayment(record.transaction_id)}
              loading={loading}
            >
              Test Pay
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Cash In Payment</Title>
        <Text type="secondary">
          Manage cash-in transactions with QRIS, Virtual Account, and E-wallet
        </Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Transactions"
              value={pagination.totalItems}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending"
              value={transactions.filter(t => t.status === 'pending').length}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Paid"
              value={transactions.filter(t => t.status === 'paid').length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={
                transactions.length > 0
                  ? Math.round((transactions.filter(t => t.status === 'paid').length / transactions.length) * 100)
                  : 0
              }
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Select
              placeholder="Status"
              style={{ width: 120 }}
              allowClear
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value || '' }))}
            >
              <Option value="pending">Pending</Option>
              <Option value="paid">Paid</Option>
              <Option value="expired">Expired</Option>
              <Option value="failed">Failed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>

            <Select
              placeholder="Payment Type"
              style={{ width: 150 }}
              allowClear
              value={filters.payment_method_type}
              onChange={(value) => setFilters(prev => ({ ...prev, payment_method_type: value || '' }))}
            >
              <Option value="qris">QRIS</Option>
              <Option value="virtual_account">Virtual Account</Option>
              <Option value="ewallet">E-wallet</Option>
            </Select>

            <DatePicker
              placeholder="From Date"
              value={filters.date_from}
              onChange={(date) => setFilters(prev => ({ ...prev, date_from: date }))}
            />

            <DatePicker
              placeholder="To Date"
              value={filters.date_to}
              onChange={(date) => setFilters(prev => ({ ...prev, date_to: date }))}
            />

            <Button onClick={() => fetchTransactions(1, filters)}>
              Search
            </Button>

            <Button onClick={() => {
              setFilters({ status: '', payment_method_type: '', date_from: null, date_to: null });
              fetchTransactions(1, { status: '', payment_method_type: '', date_from: null, date_to: null });
            }}>
              Reset
            </Button>
          </Space>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            Create Transaction
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="transaction_id"
          loading={loading}
          pagination={{
            current: pagination.currentPage,
            total: pagination.totalItems,
            pageSize: pagination.itemsPerPage,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page) => fetchTransactions(page)
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create Transaction Modal */}
      <Modal
        title="Create Cash In Transaction"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateTransaction}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="External ID"
                name="external_id"
                rules={[{ required: true, message: 'Please enter external ID' }]}
              >
                <Input placeholder="Your order/invoice ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Payment Method"
                name="payment_method_id"
                rules={[{ required: true, message: 'Please select payment method' }]}
              >
                <Select placeholder="Select payment method">
                  {Object.entries(paymentMethods).map(([type, methods]) => (
                    <Select.OptGroup key={type} label={type.replace('_', ' ').toUpperCase()}>
                      {methods.map(method => (
                        <Option key={method.id} value={method.id}>
                          {method.name} {method.fee_percentage > 0 && `(${method.fee_percentage}%)`}
                          {method.fee_fixed > 0 && ` (+Rp ${method.fee_fixed})`}
                        </Option>
                      ))}
                    </Select.OptGroup>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[
              { required: true, message: 'Please enter amount' },
              { type: 'number', min: 1000, message: 'Minimum amount is Rp 1,000' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Amount in IDR"
              formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/Rp\s?|(,*)/g, '')}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Customer Name" name="customer_name">
                <Input placeholder="Customer name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Customer Email" name="customer_email">
                <Input placeholder="customer@email.com" type="email" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Customer Phone" name="customer_phone">
            <Input placeholder="+62812345678" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea placeholder="Transaction description" rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Callback URL" name="callback_url">
                <Input placeholder="https://your-site.com/callback" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Redirect URL" name="redirect_url">
                <Input placeholder="https://your-site.com/redirect" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Expires In (minutes)" name="expires_in_minutes" initialValue={60}>
            <InputNumber min={5} max={1440} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Create Transaction
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          selectedTransaction?.transaction?.status === 'pending' && (
            <Button
              key="simulate"
              type="primary"
              onClick={() => simulatePayment(selectedTransaction.transaction.transaction_id)}
              loading={loading}
            >
              Simulate Payment
            </Button>
          ),
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedTransaction && (
          <Tabs defaultActiveKey="1">
            <TabPane tab="Transaction Info" key="1">
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Transaction ID" span={2}>
                  <Space>
                    <Text copyable={{ text: selectedTransaction.transaction.transaction_id }}>
                      {selectedTransaction.transaction.transaction_id}
                    </Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="External ID">
                  {selectedTransaction.transaction.external_id}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {getStatusTag(selectedTransaction.transaction.status)}
                </Descriptions.Item>
                <Descriptions.Item label="Payment Method" span={2}>
                  <Space>
                    {getPaymentMethodIcon(selectedTransaction.transaction.payment_method_type)}
                    {selectedTransaction.transaction.payment_method_name}
                    <Tag>{selectedTransaction.transaction.payment_method_type.replace('_', ' ').toUpperCase()}</Tag>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Amount">
                  Rp {selectedTransaction.transaction.amount?.toLocaleString('id-ID')}
                </Descriptions.Item>
                <Descriptions.Item label="Fee">
                  Rp {selectedTransaction.transaction.fee?.toLocaleString('id-ID')}
                </Descriptions.Item>
                <Descriptions.Item label="Net Amount">
                  Rp {selectedTransaction.transaction.net_amount?.toLocaleString('id-ID')}
                </Descriptions.Item>
                <Descriptions.Item label="Currency">
                  {selectedTransaction.transaction.currency}
                </Descriptions.Item>
                <Descriptions.Item label="Customer">
                  {selectedTransaction.transaction.customer_name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedTransaction.transaction.customer_email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {dayjs(selectedTransaction.transaction.created_at).format('DD/MM/YYYY HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="Expires At">
                  {dayjs(selectedTransaction.transaction.expires_at).format('DD/MM/YYYY HH:mm:ss')}
                </Descriptions.Item>
              </Descriptions>
            </TabPane>

            <TabPane tab="Payment Details" key="2">
              {selectedTransaction.transaction.payment_method_type === 'qris' && selectedTransaction.payment_details.qris && (
                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>QRIS Payment</Title>
                  <div style={{ margin: '20px 0' }}>
                    <Image
                      src={selectedTransaction.payment_details.qris.qris_image_url}
                      alt="QRIS Code"
                      width={200}
                      height={200}
                    />
                  </div>
                  <Paragraph>
                    <Text strong>Instructions:</Text><br/>
                    Scan the QR code above using any QRIS-enabled app (OVO, DANA, GoPay, etc.)
                  </Paragraph>
                  <Space>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(selectedTransaction.payment_details.qris.qris_content, 'QRIS Code')}
                    >
                      Copy QRIS Code
                    </Button>
                  </Space>
                </div>
              )}

              {selectedTransaction.transaction.payment_method_type === 'virtual_account' && selectedTransaction.payment_details.virtual_account && (
                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>Virtual Account Payment</Title>
                  <Card style={{ margin: '20px auto', maxWidth: '400px' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Bank:</Text> {selectedTransaction.payment_details.virtual_account.bank_name}
                      </div>
                      <div>
                        <Text strong>Account Number:</Text>
                        <br/>
                        <Title level={3} style={{ fontFamily: 'monospace', margin: '8px 0' }}>
                          {selectedTransaction.payment_details.virtual_account.account_number}
                        </Title>
                        <Button
                          icon={<CopyOutlined />}
                          onClick={() => copyToClipboard(selectedTransaction.payment_details.virtual_account.account_number, 'VA Number')}
                          size="small"
                        >
                          Copy
                        </Button>
                      </div>
                      <div>
                        <Text strong>Account Name:</Text> {selectedTransaction.payment_details.virtual_account.account_name}
                      </div>
                      <div>
                        <Text strong>Amount:</Text> Rp {selectedTransaction.transaction.amount?.toLocaleString('id-ID')}
                      </div>
                    </Space>
                  </Card>
                  <Paragraph>
                    <Text strong>Instructions:</Text><br/>
                    Transfer exactly <Text strong>Rp {selectedTransaction.transaction.amount?.toLocaleString('id-ID')}</Text> to the virtual account number above
                  </Paragraph>
                </div>
              )}

              {selectedTransaction.transaction.payment_method_type === 'ewallet' && selectedTransaction.payment_details.ewallet && (
                <div style={{ textAlign: 'center' }}>
                  <Title level={4}>{selectedTransaction.payment_details.ewallet.wallet_type} Payment</Title>
                  <Space direction="vertical" size="large">
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => window.open(selectedTransaction.payment_details.ewallet.deep_link)}
                    >
                      Open {selectedTransaction.payment_details.ewallet.wallet_type} App
                    </Button>
                    <Button
                      onClick={() => window.open(selectedTransaction.payment_details.ewallet.checkout_url)}
                    >
                      Pay via Web
                    </Button>
                    <Paragraph>
                      <Text strong>Instructions:</Text><br/>
                      Click the button above to pay with {selectedTransaction.payment_details.ewallet.wallet_type}
                    </Paragraph>
                  </Space>
                </div>
              )}
            </TabPane>
          </Tabs>
        )}
      </Modal>
    </div>
  );
};

export default CashIn;